import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateTime } from 'luxon';

import { Permission } from '@act/shared/authorization';
import { VISIT_INSUFFICIENT_NOTES_OUTCOMES } from '@act/shared/constants';
import {
  CreateVisitForm,
  CreateVisitSchema,
  ScheduledEventDto,
  VisitDto,
} from '@act/shared/data-transfer-objects';
import { canDeleteVisit } from '@act/shared/utilities';
import { SelectOption } from '@act/storybook';
import {
  getMemberInfo,
  membersAppSelectors,
} from 'redux/slices/memberSlice.slice';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice.slice';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hook';
import { useAppDispatch } from 'redux/storeHooks';
import { getEnvironment } from 'toolkit/environment/environment/environment.util';
import {
  DATE_TIME_FORMAT,
  visitDateTimeHelpers,
} from 'utilities/dateTimeGenerator.util';
import { buildVisitAddressOptions, resolveDefaultVisitStartTime } from '../visitDialog.util';
import {
  getVisitInteractionTimestampForFollowUp,
  shouldShowHospitalizationFollowUpCheckbox,
  useHospitalizationFollowUpDateSync,
  useHospitalizationFollowUpOutcomeSync,
} from '../hospitalizationFollowUpOutcome.util';
import { useVisitDialogPersistence } from './useVisitDialogPersistence.hook';
import { NoteFieldRef } from '../NoteField';

/** Props for {@link useVisitDialog}. */
export type UseVisitDialogProps = {
  scheduledEvent?: ScheduledEventDto;
  handleClose: () => void;
  userId: string;
  visit?: VisitDto;
  mode: 'Create' | 'Edit';
  completionCallback?: () => void;
};

/**
 * State and handlers for the visit create/edit dialog.
 * @param props Dialog props including member id, visit, and close handler.
 * @returns Form state, config inputs, and dialog action handlers.
 */
export function useVisitDialog({
  scheduledEvent,
  handleClose,
  userId,
  visit,
  mode,
  completionCallback,
}: UseVisitDialogProps) {
  const dispatch = useAppDispatch();
  const environment = getEnvironment();
  const algorithmStartDate = DateTime.fromFormat(
    environment.memberStatusAlgorithmStartDate,
    DATE_TIME_FORMAT
  );

  const user = membersAppSelectors.useSelectMemberById(userId);
  const actingUser = actingUserAppSelectors.useSelectActingUser();

  useEffect(() => {
    if (userId) {
      void dispatch(getMemberInfo({ params: { memberId: userId } }));
    }
  }, [dispatch, userId]);
  const canElevatedDelete = membersAppSelectors.useHasPermissionOnMember(
    userId,
    Permission.CAN_AUDIT_MEMBER_STATUS_CHANGE_REQUESTS
  );
  const outcomes = metaSliceSelectors.useSelectVisitOutcomes();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
  const noteFieldRef = useRef<NoteFieldRef>(null);

  const canDelete =
    !!visit?.id &&
    canDeleteVisit({
      visitStartTimeMs: visit.startTime,
      nowMs: DateTime.now().toMillis(),
      isCreator: visit.createdBy === actingUser.id,
      canElevatedDelete,
    });

  const dateOptions: SelectOption<string>[] = useMemo(
    () => visitDateTimeHelpers.getDateOptions(visit?.startTime),
    [visit?.startTime]
  );
  const startTimeOptions: SelectOption<number>[] = useMemo(
    () => visitDateTimeHelpers.getTimeOptions(visit?.startTime),
    [visit?.startTime]
  );
  const defaultStartTime = useMemo(
    () => resolveDefaultVisitStartTime(startTimeOptions),
    [startTimeOptions]
  );
  const addressOptions: SelectOption<number>[] = useMemo(
    () => buildVisitAddressOptions(user?.addresses),
    [user?.addresses]
  );

  const { control, handleSubmit, setValue, watch, formState } =
    useForm<CreateVisitForm>({
      defaultValues: {
        date: visit?.startTime
          ? visitDateTimeHelpers.getDateString(visit.startTime)
          : dateOptions[0]?.value,
        start: visit?.startTime
          ? visitDateTimeHelpers.getTimeValue(visit.startTime)
          : defaultStartTime,
        addressId:
          mode === 'Edit' ? visit?.addressId ?? -1 : addressOptions[0]?.value,
        location: visit?.location ?? null,
        note: visit?.note ?? '<p><br></p>',
        outcomeId: visit?.outcomeId ?? null,
        duration: visit?.duration ?? 15 * 60 * 1000,
        isHospitalizationFollowUp: false,
      },
      resolver: zodResolver(CreateVisitSchema),
    });

  const selectedOutcomeId = watch('outcomeId');
  const addressId = watch('addressId');
  const visitDate = watch('date');
  const visitStart = watch('start');

  const visitInteractionTimestamp = useMemo(
    () =>
      getVisitInteractionTimestampForFollowUp(
        visit?.startTime,
        visitDate,
        visitStart
      ),
    [visit?.startTime, visitDate, visitStart]
  );

  const showHospitalizationFollowUpCheckbox = useMemo(
    () =>
      shouldShowHospitalizationFollowUpCheckbox(
        user,
        visitInteractionTimestamp
      ),
    [user, visitInteractionTimestamp]
  );

  const {
    actions,
    handleDeleteVisit,
    handleFormClose,
    handleSave,
    title,
    visitNoteSaveOverride,
  } = useVisitDialogPersistence({
    user,
    visit,
    mode,
    scheduledEvent,
    handleClose,
    completionCallback,
    canDelete,
    outcomes,
    formIsDirty: formState.isDirty,
    setConfirmDeleteOpen,
    noteFieldRef,
  });

  const canEditEngagement = visit
    ? DateTime.fromMillis(visit.startTime) > algorithmStartDate
    : true;

  const outcomeOptions = metaSliceSelectors.useSelectVisitOutcomeOptions(
    'interaction',
    visit?.startTime
  );

  const outcomeHasCharacterMinimum: boolean = useMemo(() => {
    if (!outcomes || !selectedOutcomeId) {
      return false;
    }
    const outcome = outcomes.find(o => o.id === selectedOutcomeId);
    return VISIT_INSUFFICIENT_NOTES_OUTCOMES.includes(outcome.key);
  }, [outcomes, selectedOutcomeId]);

  const isHospitalizationFollowUpEnabled = useHospitalizationFollowUpOutcomeSync(
    selectedOutcomeId,
    outcomes,
    setValue
  );

  useHospitalizationFollowUpDateSync(
    user,
    visitInteractionTimestamp,
    setValue
  );

  useEffect(() => {
    if (addressId !== -1) setValue('location', null);
  }, [addressId, setValue]);

  return {
    actions,
    addressId,
    addressOptions,
    canDelete,
    canEditEngagement,
    confirmDeleteOpen,
    control,
    dateOptions,
    formState,
    handleDeleteVisit,
    handleFormClose,
    handleSave,
    handleSubmit,
    isHospitalizationFollowUpEnabled,
    noteFieldRef,
    outcomeHasCharacterMinimum,
    outcomeOptions,
    setConfirmDeleteOpen,
    showHospitalizationFollowUpCheckbox,
    startTimeOptions,
    title,
    user,
    visitNoteSaveOverride,
  };
}
